/**
 * TenderDetailPane — pane 2 of the cockpit, now a NAVIGATOR. It lists the
 * tender's facets (Suppliers & chats, Items/datasheets, Documents, Quotes) and
 * drives what the right-hand stage shows. Everything stays centered on one
 * tender: "this is the tender, this is its information, this is what I'm negotiating".
 */
import React, { useEffect, useState } from 'react';
import { FileText, Package, Tag, MessagesSquare, ChevronRight, Bot, ClipboardList, Sparkles } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { useForge } from '../ForgeContext';

const NavRow = ({ active, onClick, icon, label, sub, indent }) => (
  <button className={`forge-ck-nav ${active ? 'active' : ''} ${indent ? 'forge-ck-nav-indent' : ''}`} onClick={onClick}>
    {icon}
    <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
      <span className="forge-ck-nav-label">{label}</span>
      {sub && <span className="forge-ck-nav-sub">{sub}</span>}
    </span>
    {active && <ChevronRight size={14} />}
  </button>
);

const TenderDetailPane = ({ view, onSelect }) => {
  const { tenderId, summary } = useForge();
  const spec = summary?.spec_sheet || {};
  const lineItems = spec.line_items || [];
  const suppliers = (summary?.shortlist || []).length;
  const threads = (summary?.inbox || []).length;
  const bid = summary?.bid_summary;
  const [docCount, setDocCount] = useState(null);

  useEffect(() => {
    let on = true;
    forgeAPI.getDocuments(tenderId)
      .then((d) => on && setDocCount((d?.original?.length || 0) + (d?.working?.length || 0) + (d?.notice_url ? 1 : 0)))
      .catch(() => {});
    return () => { on = false; };
  }, [tenderId]);

  const is = (kind, id) => view?.kind === kind && (id == null || view.id === id);

  return (
    <div className="forge-cc-pane forge-cc-scroll forge-ck-detail">
      <div className="forge-ck-detail-head">
        <div style={{ fontWeight: 700 }}>{spec.title || summary?.title || tenderId}</div>
        {spec.procurement_type && <span className="forge-chip">{spec.procurement_type}</span>}
      </div>

      <NavRow active={is('agent')} onClick={() => onSelect({ kind: 'agent' })}
        icon={<Sparkles size={15} />} label="FORGE Agent" sub="ask & drive this bid" />

      <NavRow active={is('accio')} onClick={() => onSelect({ kind: 'accio' })}
        icon={<Bot size={15} />} label="Accio" sub="sourcing execution" />

      <NavRow active={is('chats')} onClick={() => onSelect({ kind: 'chats' })}
        icon={<MessagesSquare size={15} />} label="Suppliers & chats"
        sub={`${suppliers} supplier${suppliers === 1 ? '' : 's'}${threads ? ` · ${threads} chat${threads === 1 ? '' : 's'}` : ''}`} />

      <NavRow active={is('requirements')} onClick={() => onSelect({ kind: 'requirements' })}
        icon={<ClipboardList size={15} />} label="Bid requirements" sub="how to bid · docs · criteria" />

      <div className="forge-ck-sec-title"><Package size={13} /> Items / datasheets ({lineItems.length})</div>
      {lineItems.length === 0 && <div className="forge-muted" style={{ fontSize: 12, padding: '0 4px 6px' }}>No spec line items yet.</div>}
      {lineItems.map((li) => (
        <NavRow key={li.line_item} indent active={is('item', li.line_item)}
          onClick={() => onSelect({ kind: 'item', id: li.line_item })}
          icon={<span className="forge-ds-num">#{li.line_item}</span>}
          label={li.product_name}
          sub={[(li.sku ? li.sku : null), (li.required_certifications?.length ? `${li.required_certifications.length} cert${li.required_certifications.length === 1 ? '' : 's'}` : null)].filter(Boolean).join(' · ') || null} />
      ))}

      <div className="forge-ck-sec-divider" />
      <NavRow active={is('docs')} onClick={() => onSelect({ kind: 'docs' })}
        icon={<FileText size={15} />} label="Tender documents"
        sub={docCount == null ? null : `${docCount} file${docCount === 1 ? '' : 's'}`} />

      <NavRow active={is('quotes')} onClick={() => onSelect({ kind: 'quotes' })}
        icon={<Tag size={15} />} label="Quotes"
        sub={bid ? `${bid.awarded_line_items ?? 0}/${bid.total_line_items ?? lineItems.length} awarded` : null} />
    </div>
  );
};

export default TenderDetailPane;
